import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Send, XCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import {
  useTenantTickets, useTicketMessages, useSendTicketMessage, useCloseTicket,
  type SupportTicket,
} from "@/hooks/useSupportTickets";
import { ReportIssueDialog } from "@/components/controller/ReportIssueDialog";

const priorityColor: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  normal: "bg-secondary text-secondary-foreground",
  high: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  urgent: "bg-destructive text-destructive-foreground",
};

const formatTicketNumber = (n: number) => `TKT-${String(n).padStart(5, "0")}`;

const MessageThread = ({ ticketId, ticketStatus }: { ticketId: string; ticketStatus: string }) => {
  const { data: messages = [], isLoading } = useTicketMessages(ticketId);
  const sendMessage = useSendTicketMessage();
  const [reply, setReply] = useState("");

  const handleSend = async () => {
    if (!reply.trim()) return;
    try {
      await sendMessage.mutateAsync({ ticketId, body: reply.trim(), senderType: "tenant" });
      setReply("");
    } catch {
      toast.error("Failed to send message");
    }
  };

  return (
    <div className="space-y-3">
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading messages…</p>
      ) : messages.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No messages yet.</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-md p-3 text-sm ${
                m.sender_type === "admin"
                  ? "bg-primary/10 border-l-2 border-primary ml-4"
                  : "bg-muted/50 mr-4"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={m.sender_type === "admin" ? "default" : "secondary"} className="text-[10px]">
                  {m.sender_type === "admin" ? "Support" : "You"}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(m.created_at).toLocaleString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}

      {ticketStatus === "open" && (
        <div className="flex gap-2">
          <Textarea
            placeholder="Type your reply…"
            rows={2}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            className="text-sm"
          />
          <Button
            size="icon"
            className="shrink-0 self-end"
            onClick={handleSend}
            disabled={!reply.trim() || sendMessage.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const TicketRow = ({ ticket }: { ticket: SupportTicket }) => {
  const [open, setOpen] = useState(false);
  const closeTicket = useCloseTicket();

  const handleClose = async () => {
    try {
      await closeTicket.mutateAsync(ticket.id);
      toast.success("Ticket closed");
    } catch {
      toast.error("Failed to close ticket");
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <>
        <CollapsibleTrigger asChild>
          <TableRow className="cursor-pointer">
            <TableCell className="font-mono text-xs text-muted-foreground">
              {ticket.ticket_number ? formatTicketNumber(ticket.ticket_number) : "—"}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                <span className="font-medium text-sm">{ticket.subject}</span>
              </div>
            </TableCell>
            <TableCell>
              <Badge className={`${priorityColor[ticket.priority] ?? ""} text-xs capitalize`}>
                {ticket.priority}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={ticket.status === "open" ? "destructive" : "secondary"} className="text-xs capitalize">
                {ticket.status}
              </Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {new Date(ticket.created_at).toLocaleDateString()}
            </TableCell>
          </TableRow>
        </CollapsibleTrigger>
        <CollapsibleContent asChild>
          <tr>
            <td colSpan={5} className="p-0">
              <div className="bg-muted/30 px-6 py-4 space-y-3 border-b">
                {ticket.body && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Original message</p>
                    <p className="text-sm whitespace-pre-wrap">{ticket.body}</p>
                  </div>
                )}

                <MessageThread ticketId={ticket.id} ticketStatus={ticket.status} />

                {ticket.status === "open" && (
                  <div className="pt-2 border-t">
                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={handleClose}>
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Close Ticket
                    </Button>
                  </div>
                )}
              </div>
            </td>
          </tr>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
};

const SupportTickets = () => {
  const { data: tickets = [], isLoading } = useTenantTickets();
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <div className="space-y-6">
      <SEO title="My Support Tickets" description="View and track your support tickets" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Support Tickets</h1>
          <p className="text-sm text-muted-foreground">
            Track issues you've reported and view responses.
          </p>
        </div>
        <Button onClick={() => setReportOpen(true)}>New Ticket</Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="p-6 text-muted-foreground text-sm">Loading…</CardContent></Card>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            No support tickets yet. Click "New Ticket" to report an issue.
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Ticket #</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((t) => (
                <TicketRow key={t.id} ticket={t} />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ReportIssueDialog open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  );
};

export default SupportTickets;
